import { Challenge, Challenge, ChallengeCategory, CreateChallengeInput, ChallengeShare, JoinChallengeResult } from '@/types/challenge'
import { UserChallengeRepository } from './UserChallengeRepository'
import { ChallengeRepository } from './ChallengeRepository'
import { v4 as uuidv4 } from 'uuid'

// Utility functions for share tokens
function generateShareToken(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

function isValidShareToken(token: string): boolean {
  return typeof token === 'string' && token.length >= 10
}

const STORAGE_KEY = 'staychecked_guest_challenges'
const SHARES_STORAGE_KEY = 'staychecked_guest_shares'
const STORAGE_VERSION = '1.0'

interface GuestStorage {
  version: string
  challenges: Challenge[]
  lastUpdated: string
}

interface GuestSharesStorage {
  version: string
  shares: ChallengeShare[]
  lastUpdated: string
}

/**
 * Guest Challenge Adapter
 * 
 * Provides a local storage implementation for guest users
 * who want to use the app without creating an account.
 */
export class GuestChallengeAdapter implements UserChallengeRepository {
  private challengeRepository: ChallengeRepository

  constructor() {
    this.challengeRepository = new ChallengeRepository()
  }

  // Implement challenge methods by delegating to ChallengeRepository
  async getChallenges(language?: string): Promise<Challenge[]> {
    return this.challengeRepository.getChallenges(language)
  }

  async getChallenge(id: string, language?: string): Promise<Challenge | null> {
    return this.challengeRepository.getChallenge(id, language)
  }

  async getChallengesByCategory(categoryId: string, language?: string): Promise<Challenge[]> {
    return this.challengeRepository.getChallengesByCategory(categoryId, language)
  }

  async getCategories(language?: string): Promise<ChallengeCategory[]> {
    return this.challengeRepository.getCategories(language)
  }

  async getCategory(id: string, language?: string): Promise<ChallengeCategory | null> {
    return this.challengeRepository.getCategory(id, language)
  }

  // Guest-specific methods for local storage management
  private getStorage(): GuestStorage {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      if (!data) {
        return {
          version: STORAGE_VERSION,
          challenges: [],
          lastUpdated: new Date().toISOString()
        }
      }
      
      const parsed = JSON.parse(data) as GuestStorage
      
      // Handle version migrations if needed
      if (parsed.version !== STORAGE_VERSION) {
        // For now, just return empty storage for different versions
        return {
          version: STORAGE_VERSION,
          challenges: [],
          lastUpdated: new Date().toISOString()
        }
      }
      
      return parsed
    } catch (error) {
      console.warn('Failed to parse guest storage, resetting:', error)
      return {
        version: STORAGE_VERSION,
        challenges: [],
        lastUpdated: new Date().toISOString()
      }
    }
  }

  private setStorage(storage: GuestStorage): void {
    try {
      storage.lastUpdated = new Date().toISOString()
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storage))
    } catch (error) {
      console.error('Failed to save guest storage:', error)
      throw new Error('Failed to save challenge. Local storage may be full.')
    }
  }

  async create(input: CreateChallengeInput): Promise<Challenge> {
    const storage = this.getStorage()
    
    const challenge: Challenge = {
      id: uuidv4(),
      title: input.title,
      type: input.type,
      frequency: input.frequency,
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      
      // Initialize strike tracking
      currentStreak: 0,
      bestStreak: 0,
      totalCheckIns: 0,
      validatedDays: [],
    }

    storage.challenges.push(challenge)
    this.setStorage(storage)
    
    return challenge
  }

  async update(id: string, patch: Partial<Challenge>): Promise<void> {
    const storage = this.getStorage()
    const index = storage.challenges.findIndex(c => c.id === id)
    
    if (index === -1) {
      throw new Error('Challenge not found')
    }

    storage.challenges[index] = {
      ...storage.challenges[index],
      ...patch,
      updatedAt: new Date().toISOString()
    }
    
    this.setStorage(storage)
  }

  async findById(id: string): Promise<Challenge | null> {
    const storage = this.getStorage()
    return storage.challenges.find(c => c.id === id) || null
  }

  async listByOwner(): Promise<Challenge[]> {
    const storage = this.getStorage()
    return storage.challenges
  }

  async delete(id: string): Promise<void> {
    const storage = this.getStorage()
    storage.challenges = storage.challenges.filter(c => c.id !== id)
    this.setStorage(storage)
  }

  async pause(id: string): Promise<void> {
    await this.update(id, { status: 'paused' })
  }

  async pauseWithDuration(id: string, pauseUntil?: string, reason?: string): Promise<void> {
    await this.update(id, { 
      status: 'paused',
      pausedAt: new Date().toISOString(),
      pauseUntil,
      pauseReason: reason
    })
  }

  async resume(id: string): Promise<void> {
    await this.update(id, { 
      status: 'active',
      pausedAt: undefined,
      pauseUntil: undefined,
      pauseReason: undefined
    })
  }

  async migrate(userId: string): Promise<Challenge[]> {
    const storage = this.getStorage()
    const challenges = storage.challenges.map(challenge => ({
      ...challenge,
      ownerUserId: userId
    }))
    
    return challenges
  }

  async clear(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY)
  }

  // Utility method to check if guest has any challenges
  hasGuestData(): boolean {
    const storage = this.getStorage()
    return storage.challenges.length > 0
  }

  // Get challenge count for guest migration nudging
  getChallengeCount(): number {
    const storage = this.getStorage()
    return storage.challenges.length
  }

  // Sharing functionality
  private getSharesStorage(): GuestSharesStorage {
    try {
      const data = localStorage.getItem(SHARES_STORAGE_KEY)
      if (!data) {
        return {
          version: STORAGE_VERSION,
          shares: [],
          lastUpdated: new Date().toISOString()
        }
      }
      
      const parsed = JSON.parse(data) as GuestSharesStorage
      
      if (parsed.version !== STORAGE_VERSION) {
        return {
          version: STORAGE_VERSION,
          shares: [],
          lastUpdated: new Date().toISOString()
        }
      }
      
      return parsed
    } catch (error) {
      console.warn('Failed to parse guest shares storage, resetting:', error)
      return {
        version: STORAGE_VERSION,
        shares: [],
        lastUpdated: new Date().toISOString()
      }
    }
  }

  private setSharesStorage(storage: GuestSharesStorage): void {
    try {
      storage.lastUpdated = new Date().toISOString()
      localStorage.setItem(SHARES_STORAGE_KEY, JSON.stringify(storage))
    } catch (error) {
      console.error('Failed to save guest shares storage:', error)
      throw new Error('Failed to save share data. Local storage may be full.')
    }
  }

  async generateShareToken(challengeId: string): Promise<string> {
    const challenge = await this.findById(challengeId)
    if (!challenge) {
      throw new Error('Challenge not found')
    }

    // For guest mode, we'll only allow sharing if they have saved challenges
    // This encourages account creation for sharing
    const token = generateShareToken()
    const sharesStorage = this.getSharesStorage()
    
    // Remove any existing share for this challenge
    sharesStorage.shares = sharesStorage.shares.filter(s => s.challengeId !== challengeId)
    
    // Add new share
    const share: ChallengeShare = {
      id: uuidv4(),
      challengeId,
      token,
      createdAt: new Date().toISOString(),
      isActive: true,
      usageCount: 0
    }
    
    sharesStorage.shares.push(share)
    this.setSharesStorage(sharesStorage)
    
    // Update challenge to mark as shared
    await this.update(challengeId, { shareToken: token, isShared: true })
    
    return token
  }

  async findByShareToken(token: string): Promise<Challenge | null> {
    if (!isValidShareToken(token)) {
      return null
    }

    const sharesStorage = this.getSharesStorage()
    const share = sharesStorage.shares.find(s => s.token === token && s.isActive)
    
    if (!share) {
      return null
    }

    return this.findById(share.challengeId)
  }

  async validateCheckIn(id: string, checkInDate?: string): Promise<Challenge> {
    const challenge = await this.findById(id)
    
    if (!challenge) {
      throw new Error('Challenge not found')
    }
    
    if (challenge.status !== 'active') {
      throw new Error('Cannot validate check-in for inactive challenge')
    }
    
    const now = new Date().toISOString()
    const checkInDateObj = checkInDate ? new Date(checkInDate) : new Date()
    const checkInDateStr = checkInDateObj.toISOString().split('T')[0] // YYYY-MM-DD format
    
    // Check if this day has already been validated
    const validatedDays = challenge.validatedDays || []
    if (validatedDays.includes(checkInDateStr)) {
      throw new Error('This day has already been validated')
    }
    
    const updatedChallenge: Challenge = {
      ...challenge,
      currentStreak: challenge.currentStreak + 1,
      bestStreak: Math.max(challenge.bestStreak, challenge.currentStreak + 1),
      totalCheckIns: challenge.totalCheckIns + 1,
      lastCheckIn: checkInDate || now,
      validatedDays: [...validatedDays, checkInDateStr],
      updatedAt: now
    }
    
    const storage = this.getStorage()
    const index = storage.challenges.findIndex(c => c.id === id)
    if (index === -1) {
      throw new Error('Challenge not found in storage')
    }
    
    storage.challenges[index] = updatedChallenge
    this.setStorage(storage)
    
    return updatedChallenge
  }

  async joinChallenge(token: string, userId?: string): Promise<JoinChallengeResult> {
    const challenge = await this.findByShareToken(token)
    
    if (!challenge) {
      return {
        success: false,
        error: 'Challenge not found or share link has expired'
      }
    }

    if (challenge.status !== 'active') {
      return {
        success: false,
        error: 'This challenge is no longer active'
      }
    }

    try {
      // Clone the challenge for the new user
      const clonedChallenge: Challenge = {
        ...challenge,
        id: uuidv4(),
        ownerUserId: userId, // Will be undefined for guests
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        shareToken: undefined, // New challenge doesn't inherit share token
        isShared: false,
        
        // Reset strike tracking for new participant
        currentStreak: 0,
        bestStreak: 0,
        totalCheckIns: 0,
        lastCheckIn: undefined,
        validatedDays: [],
      }

      // Save the cloned challenge
      const storage = this.getStorage()
      storage.challenges.push(clonedChallenge)
      this.setStorage(storage)

      // Update usage count for the share
      const sharesStorage = this.getSharesStorage()
      const share = sharesStorage.shares.find(s => s.token === token)
      if (share) {
        share.usageCount += 1
        this.setSharesStorage(sharesStorage)
      }

      return {
        success: true,
        challenge: clonedChallenge
      }
    } catch (error) {
      return {
        success: false,
        error: 'Failed to join challenge. Please try again.'
      }
    }
  }

  async revokeShareToken(challengeId: string): Promise<void> {
    const sharesStorage = this.getSharesStorage()
    const shareIndex = sharesStorage.shares.findIndex(s => s.challengeId === challengeId)
    
    if (shareIndex !== -1) {
      sharesStorage.shares[shareIndex].isActive = false
      this.setSharesStorage(sharesStorage)
    }

    // Update challenge to remove share info
    await this.update(challengeId, { shareToken: undefined, isShared: false })
  }

} 